import React from 'react';
import * as Yup from 'yup';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Collapse,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import { string } from 'yup';
import {
  JunoUser,
  Reason9fdEnum as ReasonEnum,
  UserDeactivation,
  UserDeactivationReasonEnum,
} from '@juno/client-api/model';
import { useSettings } from '@juno/utils';
import DialogAriaWrapper from '../DialogAriaWrapper';

const validationSchema = Yup.object().shape({
  description: string()
    .max(400, 'Character limit: 400')
    .when('reason', { is: ReasonEnum.Other, then: string().required('Description is required') }),
});

type ExtendedUserDeactivation = UserDeactivation & { description: string };

interface DeactivateUserDialogProps {
  user: JunoUser;
  handleSubmit: (values: UserDeactivation) => void;
  handleClose: () => void;
  open: boolean;
  isBulkEditing: boolean;
  isSubmitting: boolean;
  successfullySubmitted: boolean;
}

const DeactivateUserDialog: React.FC<DeactivateUserDialogProps> = ({
  user,
  handleSubmit,
  handleClose,
  open,
  isBulkEditing,
  isSubmitting,
  successfullySubmitted,
}) => {
  const { site } = useSettings();

  const initialValues = {
    id: '',
    platform: site?.platform_id,
    users: [user.id],
    reason: UserDeactivationReasonEnum.Data_Cleanup,
    description: '',
  } as ExtendedUserDeactivation;

  return (
    <DialogAriaWrapper open={open} onClose={handleClose} id='deactivate_users_dialog'>
      <Formik
        key={'deactivate-users-form'}
        enableReinitialize
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={(values) => handleSubmit(values)}
      >
        {(formik) => {
          const { values, setFieldValue, setTouched, isValid } = formik;
          const charactersLeft = 400 - (values.description?.length || 0);

          return (
            <Form>
              <Fade in={!successfullySubmitted}>
                <DialogTitle
                  sx={{
                    fontWeight: 600,
                    borderBottom: `1px solid #cac4d0`,
                  }}
                >
                  {isBulkEditing
                    ? 'Why are you deactivating these users?'
                    : `Why are you deactivating ${user.first_name} ${user.last_name}?`}
                </DialogTitle>
              </Fade>
              <DialogContent sx={{ width: 600, maxWidth: '85vw' }}>
                <Fade in={!successfullySubmitted}>
                  <FormControl sx={{ py: 2, width: '100%' }}>
                    <RadioGroup
                      aria-labelledby='report-user-form'
                      defaultValue={UserDeactivationReasonEnum.Data_Cleanup}
                      name='radio-buttons-group'
                      onChange={(e) => {
                        setFieldValue('description', '');
                        setTouched({ description: false });
                        setFieldValue('reason', e.target.value);
                      }}
                      sx={{
                        width: '100%',
                        px: 1,
                        '.MuiFormControlLabel-label': {
                          fontWeight: 600,
                        },
                      }}
                    >
                      {(
                        Object.keys(UserDeactivationReasonEnum) as Array<
                          keyof typeof UserDeactivationReasonEnum
                        >
                      ).map((r) => (
                        <DeactivateItem
                          key={r}
                          reason={UserDeactivationReasonEnum[r]}
                          charactersLeft={charactersLeft}
                          values={values}
                          setFieldValue={setFieldValue}
                          setTouched={setTouched}
                          errors={formik.errors}
                          touched={formik.touched}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Fade>
                <Fade
                  in={successfullySubmitted && !isSubmitting}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%,-50%)',
                    width: '80%',
                  }}
                >
                  <Box textAlign='center'>
                    <Typography textAlign={'center'} mb={2}>
                      {isBulkEditing
                        ? 'Selected users have been deactivated.'
                        : `${user.first_name} ${user.last_name} has been deactivated.`}
                    </Typography>
                    <Button onClick={handleClose} variant='outlined' sx={{ width: 167 }}>
                      Close
                    </Button>
                  </Box>
                </Fade>
              </DialogContent>
              <Fade in={!successfullySubmitted}>
                <DialogActions
                  sx={{
                    justifyContent: 'space-between',
                    pr: 2,
                    pl: 2,
                    py: 2,
                    borderTop: (theme) => `1px solid #cac4d0`,
                  }}
                >
                  <LoadingButton
                    fullWidth
                    type='submit'
                    disabled={!isValid || successfullySubmitted}
                    variant={'contained'}
                    loading={isSubmitting}
                  >
                    Submit
                  </LoadingButton>
                  <Button fullWidth onClick={handleClose} variant='outlined'>
                    Cancel
                  </Button>
                </DialogActions>
              </Fade>
            </Form>
          );
        }}
      </Formik>
    </DialogAriaWrapper>
  );
};

export default DeactivateUserDialog;

interface DeactivateItemProps {
  reason: ReasonEnum | UserDeactivationReasonEnum;
  charactersLeft: number;
  values: FormikProps<ExtendedUserDeactivation>['values'];
  setFieldValue: FormikProps<ExtendedUserDeactivation>['setFieldValue'];
  setTouched: FormikProps<ExtendedUserDeactivation>['setTouched'];
  errors: FormikProps<ExtendedUserDeactivation>['errors'];
  touched: FormikProps<ExtendedUserDeactivation>['touched'];
}

const DeactivateItem: React.FC<DeactivateItemProps> = ({
  reason,
  charactersLeft,
  values,
  setFieldValue,
  setTouched,
  errors,
  touched,
}) => {
  return (
    <>
      <FormControlLabel
        value={reason}
        control={<Radio />}
        disableTypography
        label={
          <Stack direction='row' alignItems='center' justifyContent='space-between' width='100%'>
            <Typography fontWeight='bold'>{reason}</Typography>
            <ArrowRightIcon
              sx={{
                mr: -2,
                transition: '200ms',
                transform: values.reason === reason ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            />
          </Stack>
        }
      />
      <Collapse
        in={values.reason === reason}
        sx={{
          mx: -1,
        }}
      >
        <Fade in={values.reason === reason}>
          <Box sx={{ mb: 3 }}>
            <TextField
              multiline
              rows={4}
              fullWidth
              inputProps={{ style: { fontSize: 15 } }}
              value={values.description}
              onChange={(e) => {
                setTouched({ description: true });
                setFieldValue('description', e.target.value);
              }}
              error={touched.description && !!errors.description}
              helperText={touched.description && errors.description}
              placeholder={`Please provide any additional information that may help us understand why you are deactivating this user (${
                reason === ReasonEnum.Other ? 'Required' : 'Optional'
              }).`}
            />
            <Stack alignItems='flex-end'>
              <Typography variant='body2'>
                {charactersLeft >= 0 ? charactersLeft : 0} characters left
              </Typography>
            </Stack>
          </Box>
        </Fade>
      </Collapse>
    </>
  );
};
